import {
    selectExcludedTopicsForAnswer,
    selectExistingTopicsForAnswer,
    selectTopicDetails,
    selectIncludedTopicNames,
} from "features/scorable/scorableSlice";
import { selectExcludedTopicNames } from "features/topics/topicsSlice";
import embeddingService from "services/embeddingService";
import { shallowEqual } from "react-redux";
import { GuxTooltip } from "genesys-spark-components-react";
import "./AssistedMCQOption.less";
import strings from "./AssistedMCQOption.strings";
import commonStrings from "features/qualityForm/common/questionComponents/CommonQuestionComponents.strings";
import { getScoreValueAriaLabel } from "util/componentUtil";
import { TranscriptPhrase } from "./TranscriptPhrase";
import { useStrings } from "features/i18n/useStrings";
import { ScoreTag, ScoreTagLevel } from "features/common/ScoreTag";
import { useAppSelector } from "app/hooks";
import { TranscriptTopic } from "@genesys/cloud-frontend/dist/mjs/models/TranscriptTopic";
import { selectCanUserViewSTATopics } from "features/auth/currentUserSlice";
import { MCQOptionRadioInput } from "../MCQOptionRadioInput";

// Feature slice commons
import { selectAnswerOption } from "features/qualityForm/qualityFormSlices/qualityFormCommons";

export function AssistedMCQOption({
    answerOptionId,
    selected,
    disabled,
    questionLevel = "default",
    onSelected,
    hideScore = false,
    focusLabelText,
}: {
    answerOptionId: string;
    selected: boolean;
    disabled: boolean;
    questionLevel?: ScoreTagLevel;
    onSelected: (s: string) => void;
    hideScore?: boolean;
    focusLabelText?: string | null;
}) {
    const commonT = useStrings(commonStrings);
    const { text, value } = useAppSelector((state) => {
        const { text, value } = selectAnswerOption(state, answerOptionId) ?? {
            text: "",
            value: "",
        };
        return { text, value };
    }, shallowEqual);
    const canViewTopics = useAppSelector(selectCanUserViewSTATopics);

    const existingTopics = useAppSelector((state) => {
        const topics = selectExistingTopicsForAnswer(answerOptionId, state);
        return topics && topics.length > 0 ? topics : undefined;
    }, shallowEqual);

    const excludedTopics = useAppSelector((state) => {
        const topics = selectExcludedTopicsForAnswer(answerOptionId, state);
        return topics && topics.length > 0 ? topics : undefined;
    }, shallowEqual);

    const existingTopicPhraseDetails = useAppSelector((state) => {
        if (existingTopics) {
            return selectTopicDetails(state, existingTopics);
        }
    }, shallowEqual);

    return (
        <div className="AssistedMCQOption">
            <div
                className={`MultipleChoiceQuestionOption ${
                    selected ? "selected" : ""
                }`}
            >
                <MCQOptionRadioInput
                    answerOptionId={answerOptionId}
                    selected={selected}
                    disabled={disabled}
                    onSelected={onSelected}
                    text={text}
                    type="evaluation-assistance"
                />
                <div className="assisted-answer-details">
                    {canViewTopics && (
                        <div>
                            <TopicSummary
                                existingTopics={existingTopics}
                                excludedTopics={excludedTopics}
                                phraseCount={
                                    existingTopicPhraseDetails?.length ?? 0
                                }
                                focusLabelText={focusLabelText}
                            />
                        </div>
                    )}

                    {disabled && !hideScore && (
                        <div className="score">
                            <ScoreTag
                                mode={questionLevel}
                                ariaLabel={getScoreValueAriaLabel(
                                    commonT.labels,
                                    questionLevel,
                                    value ?? 0
                                )}
                            >
                                {value}
                            </ScoreTag>
                        </div>
                    )}
                </div>
            </div>
            {canViewTopics && existingTopicPhraseDetails && existingTopics && (
                <TopicList
                    hideTopicName={existingTopics?.length <= 1}
                    topicDetails={existingTopicPhraseDetails}
                />
            )}
        </div>
    );
}

export function TopicSummary({
    phraseCount,
    existingTopics,
    excludedTopics,
    focusLabelText,
}: {
    phraseCount: number;
    existingTopics?: string[];
    excludedTopics?: string[];
    focusLabelText?: string | null;
}) {
    const t = useStrings(strings);
    const topicCount = existingTopics?.length ?? 0;
    return (
        <div className="TopicSummary" tabIndex={0} data-testid="topic-summary">
            <div className="topic-summary-label">
                {topicCount > 1 && (
                    <span className="topic-count">
                        {t.topics({ count: topicCount })}{" "}
                    </span>
                )}
                <span className="phrase-count">
                    {t.phrases({ count: phraseCount })}
                </span>
            </div>
            {focusLabelText && (
                <div className="focus-label-group">
                    <div className="vertical-line" />
                    <span
                        className="focus-label"
                        data-testid="evaluation-focus-label"
                    >
                        {focusLabelText}
                    </span>
                </div>
            )}
            <GuxTooltip>
                <TopicSummaryToolTip
                    existingTopics={existingTopics}
                    excludedTopics={excludedTopics}
                />
            </GuxTooltip>
        </div>
    );
}

function TopicSummaryToolTip({
    existingTopics,
    excludedTopics,
}: {
    existingTopics?: string[];
    excludedTopics?: string[];
}) {
    const t = useStrings(strings);
    const deletedTopicPlaceholder = t.labels.sbDeletedTopic;

    const { existingNames, excludedNames } = useAppSelector((state) => {
        return {
            existingNames:
                selectIncludedTopicNames(state, existingTopics) ?? "",
            excludedNames:
                selectExcludedTopicNames(
                    state,
                    deletedTopicPlaceholder,
                    excludedTopics
                ) ?? "",
        };
    }, shallowEqual);

    const toolTipCard = (
        heading: string,
        topicCount: number,
        topicNames: string
    ) => {
        return (
            <div>
                <div className="heading">{heading}</div>
                <div className="content">
                    <span className="sub-heading">
                        {topicCount === 1
                            ? t.topic({ count: topicCount })
                            : t.topics({ count: topicCount })}
                    </span>
                    <span> {topicNames}</span>
                </div>
            </div>
        );
    };
    return (
        <div className="TopicSummaryToolTip">
            {existingTopics &&
                existingTopics?.length > 0 &&
                toolTipCard(
                    t.labels.included,
                    existingTopics.length,
                    existingNames
                )}
            {excludedTopics &&
                excludedTopics?.length > 0 &&
                toolTipCard(
                    existingTopics?.length
                        ? t.labels.andExcluded
                        : t.labels.excluded,
                    excludedTopics.length,
                    excludedNames
                )}
        </div>
    );
}

export function TopicList({
    hideTopicName,
    topicDetails,
}: {
    hideTopicName: boolean;
    topicDetails: TranscriptTopic[];
}) {
    const t = useStrings(strings);
    const mediaType = useAppSelector(
        (state) => state.scorable.data.scorable?.mediaType
    );

    //TODO : pass offset words to  postScrollTranscriptToEmailPhrase instead of 0
    const jumpToPhrase = (topicDetail: TranscriptTopic) => {
        if (
            mediaType &&
            mediaType[0].toLowerCase() === "email" &&
            topicDetail.id &&
            topicDetail.startTimeMilliseconds
        ) {
            embeddingService.postScrollTranscriptToEmailPhrase(
                topicDetail.offset?.wordCount ?? 0,
                topicDetail.topicPhrase,
                topicDetail.startTimeMilliseconds
            );
        } else {
            topicDetail?.recordingLocation &&
                embeddingService.postScrollTranscriptToPhrase(
                    topicDetail.recordingLocation
                );
        }
    };

    return (
        <div className="TopicList" data-testid="topic-list">
            {topicDetails.map((topicDetail) => {
                return (
                    <div className="list-item">
                        {!hideTopicName && (
                            <span className="topic">
                                {topicDetail.name + ": "}
                            </span>
                        )}
                        <span
                            className="phrase Clickable"
                            onClick={() => jumpToPhrase(topicDetail)}
                        >
                            <TranscriptPhrase
                                transcriptPhrase={topicDetail.transcriptPhrase}
                                recognizedEntityTypes={
                                    t.recognizedNamedEntities
                                }
                            />
                        </span>
                    </div>
                );
            })}
        </div>
    );
}
